import { filterByAppType, useQueryGetAllBots } from "@apis:bot/bot";
import { PageKey } from "@apis:ui_branding/ui_branding";
import { useBranding } from "@app-setup:BrandingContext";
import { SVGInternalIcon } from "@components:SVGInternalIcon";
import { useNewChat } from "@hooks:useNewChat";
import { useRBACCheck } from "@hooks:useRBAC";
import { Box, IconButton } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { useSidebarActions } from "@stores:sidebar";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import { AppType } from "@:constants";
import { FeatureFlag } from "@:flag";
import { RBACAction, RBACResource } from "@:rbac";

type SwitchPageProps = {
  disablePageClick?: boolean;
};

// Helper to render icon (custom SVG or preset)
const PageIcon = ({
  page,
  defaultIcon,
  disabled,
  size = 28,
  color,
}: {
  page: PageKey;
  defaultIcon: Parameters<typeof SVGInternalIcon>[0]["icon"];
  disabled: boolean;
  size?: number;
  color?: string;
}) => {
  const { getPageConfig } = useBranding();
  const pageConfig = getPageConfig(page);

  // Custom SVG takes priority
  if (pageConfig?.customIconSvg) {
    return (
      <Box
        component="img"
        src={pageConfig.customIconSvg}
        alt={page}
        sx={{
          width: size,
          height: size,
          objectFit: "contain",
          opacity: disabled ? 0.3 : 1,
        }}
      />
    );
  }

  // Preset icon (either custom or default)
  const iconKey = pageConfig?.iconKey || defaultIcon;
  return (
    <SVGInternalIcon
      icon={iconKey as Parameters<typeof SVGInternalIcon>[0]["icon"]}
      sx={{
        width: size,
        height: size,
        opacity: disabled ? 0.3 : 1,
        color: color,
      }}
    />
  );
};

export const SwitchPage = ({ disablePageClick = true }: SwitchPageProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { newChat } = useNewChat();
  const { getPageConfig } = useBranding();
  const canAccessAnalytics = useRBACCheck(
    RBACAction.read,
    RBACResource.analytics,
  );
  const sidebarActions = useSidebarActions();

  const { pathname } = useLocation();

  // Currently we can only can check bots to see if user can access pages
  const { data: allBots } = useQueryGetAllBots(true);

  const pages: Record<string, string> = {
    chat: "/app/chat",
    search: "/app/search",
    docs: "/app/docs",
    analytics: "/app/analytics",
    audio: "/app/audio",
    documentTranslation: "/app/document-translation",
  };

  const disablePage = Object.keys(pages).reduce(
    (acc, key) => {
      acc[key] = pathname.includes(pages[key]) && disablePageClick;
      return acc;
    },
    {} as Record<keyof typeof pages, boolean>,
  );

  // Helper to get tooltip text
  const getTooltip = (page: PageKey, defaultText: string) => {
    return getPageConfig(page)?.tooltip || defaultText;
  };

  return (
    <Box>
      {allBots && filterByAppType(allBots, AppType.Search).length > 0 && (
        <Tooltip
          title={
            disablePage.chat || disablePage.search
              ? null
              : getTooltip("chat", t("nav.goToTalk"))
          }
        >
          <span>
            <IconButton
              disabled={disablePage.chat || disablePage.search}
              sx={{ width: "50px", height: "50px" }}
              onClick={() => {
                newChat();
                sidebarActions.showSidebar(false);
              }}
            >
              <PageIcon
                page="chat"
                defaultIcon="botNav"
                disabled={disablePage.chat || disablePage.search}
              />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {allBots &&
        filterByAppType(allBots, AppType.Docs).length > 0 &&
        FeatureFlag.Docs && (
          <Tooltip
            title={
              disablePage.docs ? null : getTooltip("docs", t("nav.goToDocs"))
            }
          >
            <span>
              <IconButton
                onClick={() => {
                  navigate("/app/docs", { replace: true });
                }}
                disabled={disablePage.docs}
                sx={{ width: "50px", height: "50px" }}
              >
                <PageIcon
                  page="docs"
                  defaultIcon="docsNav"
                  disabled={disablePage.docs}
                />
              </IconButton>
            </span>
          </Tooltip>
        )}

      {allBots &&
        filterByAppType(allBots, AppType.Audio).length > 0 &&
        FeatureFlag.Audio && (
          <Tooltip
            title={
              disablePage.audio ? null : getTooltip("audio", t("nav.goToAudio"))
            }
          >
            <span>
              <IconButton
                onClick={() => {
                  navigate("/app/audio", { replace: true });
                }}
                disabled={disablePage.audio}
                sx={{ width: "50px", height: "50px" }}
              >
                <PageIcon
                  page="audio"
                  defaultIcon="audio"
                  disabled={disablePage.audio}
                  color="#171546"
                />
              </IconButton>
            </span>
          </Tooltip>
        )}

      {allBots &&
        filterByAppType(allBots, AppType.Translation).length > 0 &&
        FeatureFlag.Translation && (
          <Tooltip
            title={
              disablePage.documentTranslation
                ? null
                : getTooltip("translation", t("nav.goToTranslation"))
            }
          >
            <span>
              <IconButton
                onClick={() => {
                  navigate("/app/document-translation", { replace: true });
                }}
                disabled={disablePage.documentTranslation}
                sx={{ width: "50px", height: "50px" }}
              >
                <PageIcon
                  page="translation"
                  defaultIcon="docTranslate"
                  disabled={disablePage.documentTranslation}
                  color="#171546"
                />
              </IconButton>
            </span>
          </Tooltip>
        )}

      {FeatureFlag.Analytics && canAccessAnalytics && (
        <Tooltip
          title={
            disablePage.analytics
              ? null
              : getTooltip("analytics", t("nav.goToAnalytics"))
          }
        >
          <span>
            <IconButton
              disabled={disablePage.analytics}
              onClick={() => {
                navigate("/app/analytics", { replace: true });
              }}
            >
              <PageIcon
                page="analytics"
                defaultIcon="analyticsNav"
                disabled={disablePage.analytics}
                size={23}
              />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};
